import React, { useState } from "react";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Paginator } from "primereact/paginator";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";
import { ToFormatedDateForEvents } from "../../../../utils/dates";
import { getEventTypeDisplayProps } from "../../../../utils/eventTypes";
import eventsAPI from "../../../../services/events";

const PAGE_SIZE = 30;
const MAX_EVENTS = 1000;
const EVENT_TYPES = "MEETING,VACATION,BANK_HOLIDAY,OTHER";

// Read-only agenda of a training: past events hidden by default and greyed out when shown.
// Render with key={trainingId} so pagination and the past-events toggle reset per training.
const AgendaEventsComponent = ({ schoolId, trainingId, accessToken }) => {
  const [page, setPage] = useState(1);
  const [showPastEvents, setShowPastEvents] = useState(false);

  const eventsQuery = eventsAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    MAX_EVENTS,
    { eventType: EVENT_TYPES }
  );
  const allEvents = eventsQuery.data?.data || [];
  const now = new Date();
  const upcomingEvents = allEvents.filter((event) => new Date(event.end_date) >= now);
  const pastEventsCount = allEvents.length - upcomingEvents.length;
  const visibleEvents = showPastEvents ? allEvents : upcomingEvents;
  const events = visibleEvents.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!trainingId) {
    return null;
  }

  return (
    <>
      {eventsQuery.isSuccess && pastEventsCount > 0 && (
        <Button
          label={showPastEvents
            ? "Masquer les événements passés"
            : `Voir les événements passés (${pastEventsCount})`}
          text
          size="small"
          className="mb-3"
          onClick={() => {
            setShowPastEvents((current) => !current);
            setPage(1);
          }}
        />
      )}
      {eventsQuery.isLoading && <Skeleton width="100%" height="200px" />}
      {eventsQuery.isError && (
        <Message severity="error" text="Impossible de charger l'agenda de cette formation." />
      )}
      {eventsQuery.isSuccess && events.length === 0 && (
        <Message
          severity="info"
          text={showPastEvents
            ? "Aucun événement dans l'agenda de cette formation."
            : "Aucun événement à venir dans l'agenda de cette formation."}
        />
      )}
      {eventsQuery.isSuccess && events.length > 0 && (
        <>
          <div className="flex flex-column gap-3">
            {events.map((event) => {
              const isPast = new Date(event.end_date) < now;
              const tagProps = getEventTypeDisplayProps(event.event_type, event.start_date, event.end_date);
              return (
                <div
                  key={event.id}
                  data-testid={`agenda-event-${event.id}`}
                  data-past={isPast}
                  className={`border-round p-4 ${isPast ? "surface-100 border-1 surface-border" : "surface-card shadow-1"}`}
                >
                  <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                    <span className={`text-lg font-medium ${isPast ? "text-500" : "text-900"}`}>
                      {event.name}
                    </span>
                    <div className="flex align-items-center gap-2">
                      {isPast && <span className="text-500 text-sm font-medium">Passé</span>}
                      <Tag
                        {...tagProps}
                        style={isPast
                          ? { ...tagProps.style, backgroundColor: "var(--gray-400)" }
                          : tagProps.style}
                      />
                    </div>
                  </div>
                  <div className={isPast ? "text-500" : "text-600"}>
                    {ToFormatedDateForEvents(new Date(event.start_date), event.all_day)}
                    {" — "}
                    {ToFormatedDateForEvents(new Date(event.end_date), event.all_day)}
                  </div>
                  {event.presentation && (
                    <div className={`mt-2 ${isPast ? "text-500" : "text-700"}`}>{event.presentation}</div>
                  )}
                </div>
              );
            })}
          </div>
          {visibleEvents.length > PAGE_SIZE && (
            <Paginator
              first={(page - 1) * PAGE_SIZE}
              rows={PAGE_SIZE}
              totalRecords={visibleEvents.length}
              onPageChange={(event) => setPage(event.page + 1)}
            />
          )}
        </>
      )}
    </>
  );
};

export default AgendaEventsComponent;
